import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { Map as MapGl, type MapRef } from 'react-map-gl/mapbox';
import { useSearchParams } from 'react-router-dom';

import ConfigurationButton from '@/components/map/configuration-button';
import MapConfigError from '@/components/map/map-config-error';
import MapLayers from '@/components/map/map-layers';
import RecenterButton from '@/components/map/recenter-button';
import ResetButton from '@/components/map/reset-button';
import ZoomButtons from '@/components/map/zoom-buttons';
import useMapBounds from '@/hooks/use-map-bounds';
import useMapEvents from '@/hooks/use-map-events';
import useMapImageLoader from '@/hooks/use-map-image-loader';
import useMapInteractivity from '@/hooks/use-map-interactivity';
import useNetworkStatusMonitor from '@/hooks/use-network-status-monitor';
import { cn } from '@/lib/utils';
import { INTERACTIVE_LAYERS, MAPBOX_INITIAL_VIEW_STATE, MAX_ZOOM_IN } from '@/utils/constants/map';
import {
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_VEREINA_STATUS,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';
import getNetworkStatusMessage from '@/utils/helpers/get-network-status-message';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapProps {
  shouldRenderMap: boolean;
  isMapVisible: boolean;
  onMapReady: () => void;
  isMapReady: boolean;
  isDrawerOpen?: boolean;
  isToastVisible?: boolean;
  drawerHeight?: number;
}

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const MAPBOX_STYLE_URL = import.meta.env.VITE_MAPBOX_STYLE_URL;

const Map: FC<MapProps> = ({ shouldRenderMap, isMapVisible, onMapReady, isMapReady }) => {
  const [, setSearchParams] = useSearchParams();
  const [hasMapError, setHasMapError] = useState(false);

  const networkStatus = useNetworkStatusMonitor();

  const mapRef = useRef<MapRef>(null);

  useMapImageLoader(mapRef, isMapVisible);
  useMapInteractivity(mapRef, isMapVisible);

  const { initialZoom, isConfigured } = useMapBounds(mapRef, isMapVisible, shouldRenderMap);

  const updateSearchParams = useCallback((type: string, id: string, paramsToShow: string) => {
    const newSearchParams = new URLSearchParams(window.location.search);

    const selectionParams = [
      SEARCH_PARAM_STATION_ID,
      SEARCH_PARAMS_ROUTE_ID,
      SEARCH_PARAM_VEREINA_STATUS,
    ];

    selectionParams.forEach((param) => {
      newSearchParams.delete(param);
    });

    newSearchParams.set(type, id);
    newSearchParams.set(SEARCH_PARAM_SHOW, paramsToShow);

    setSearchParams(newSearchParams);
  }, []);

  const { selectMapMarker } = useMapEvents({ updateSearchParams });

  const handleMapLoad = useCallback(() => {
    onMapReady();

    const map = mapRef.current?.getMap();

    if (map) {
      // kill the two-finger rotation gesture on mobile touch screens
      map.touchZoomRotate.disableRotation();
    }
  }, [onMapReady]);

  const handleMapError = useCallback(() => {
    setHasMapError(true);
  }, []);

  useEffect(() => {
    if (shouldRenderMap && mapRef.current) {
      mapRef.current.getMap().resize();
    }
  }, [shouldRenderMap]);

  useEffect(() => {
    getNetworkStatusMessage(networkStatus);
  }, [networkStatus]);

  if (hasMapError) {
    return <MapConfigError />;
  }

  return (
    <div
      className={cn('fixed size-full top-9', {
        'opacity-0 pointer-events-none -z-10': !shouldRenderMap,
      })}
    >
      <MapGl
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        initialViewState={MAPBOX_INITIAL_VIEW_STATE}
        projection="globe"
        mapStyle={MAPBOX_STYLE_URL}
        onLoad={handleMapLoad}
        onError={handleMapError}
        onClick={selectMapMarker}
        interactiveLayerIds={INTERACTIVE_LAYERS}
        pitchWithRotate={false}
        minPitch={0}
        maxPitch={0}
        dragRotate={false}
        touchPitch={false}
        maxZoom={MAX_ZOOM_IN}
        interactive={true}
      >
        {isMapVisible && isConfigured && isMapReady && <MapLayers />}

        <div className="fixed top-17.5 left-2.5 z-50 flex flex-col gap-2">
          <ZoomButtons />
          <ResetButton initialZoom={initialZoom} />
          <RecenterButton />
          <ConfigurationButton />
        </div>
      </MapGl>
    </div>
  );
};

export default Map;
